import {
  getFontCss,
  HoverableLink,
  HoverableLinkSizes,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import FloatingToolbar from 'src/components/FloatingToolbar/FloatingToolbar'
import MoreDropdown, {
  MoreDropdownDesigns,
} from 'src/components/MoreDropdown/MoreDropdown'
import SvgClose from 'src/components/Svgs/SvgClose'
import SvgCloseCard from 'src/components/Svgs/SvgCloseCard'
import SvgSlashCircle from 'src/components/Svgs/SvgSlashCircle'
import TestId from 'src/components/TestId/TestId'
import { BulkAction } from 'src/hooks/bulkActionHooks'
import { translateSingleString } from 'src/localization/Trns'
import { VirtualCardListModel } from 'src/models/virtualCard.models'
import { css } from 'styled-components'

export type VirtualCardsBulkActionsToolbarProps = {
  selectedVirtualCards: VirtualCardListModel[]
  onUpdate: () => void
  onDeactivate: () => void
  onClose: () => void
  onClearSelection: () => void
  locale: string
}

const selectedVirtualCardsText = ({
  selectedVirtualCards,
  locale,
}: {
  selectedVirtualCards: VirtualCardListModel[]
  locale: string
}): React.ReactElement => {
  let standardCount = 0
  let recurringCount = 0
  let standardGuestCount = 0
  let recurringGuestCount = 0
  const cardTypeArray = []

  selectedVirtualCards.forEach((virtualCard) => {
    if (virtualCard.recurs) {
      if (virtualCard.direct) {
        recurringGuestCount++
      } else {
        recurringCount++
      }
    } else if (virtualCard.direct) {
      standardGuestCount++
    } else {
      standardCount++
    }
  })

  if (standardCount > 0) {
    cardTypeArray.push(
      translateSingleString(locale, '${num} standard', {
        num: standardCount.toString(),
      }),
    )
  }
  if (recurringCount > 0) {
    cardTypeArray.push(
      translateSingleString(locale, '${num} recurring', {
        num: recurringCount.toString(),
      }),
    )
  }
  if (standardGuestCount > 0) {
    cardTypeArray.push(
      translateSingleString(locale, '${num} guest', {
        num: standardGuestCount.toString(),
      }),
    )
  }
  if (recurringGuestCount > 0) {
    cardTypeArray.push(
      translateSingleString(locale, '${num} recurring guest', {
        num: recurringGuestCount.toString(),
      }),
    )
  }

  return (
    <TestId testId={'selectedCardAmount'}>
      {translateSingleString(locale, '${cardTypes} selected', {
        cardTypes: cardTypeArray.join(', ').toString(),
      })}
    </TestId>
  )
}

const VirtualCardsBulkActionsToolbar: React.FC<
  VirtualCardsBulkActionsToolbarProps
> = (props) => {
  return (
    <FloatingToolbar
      visible={props.selectedVirtualCards.length > 0}
      css={css`
        > div {
          column-gap: 20px;
          align-items: stretch;
        }
      `}
    >
      <span
        css={css`
          align-items: center;
          ${getFontCss(
            16,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.medium,
          )};
          white-space: nowrap;
          display: inline-flex;
        `}
      >
        {selectedVirtualCardsText({
          selectedVirtualCards: props.selectedVirtualCards,
          locale: props.locale,
        })}
      </span>
      <TestId
        testId={'bulkUpdateButton'}
        css={css`
          display: flex;
          align-items: center;
          padding-left: 20px;
          border-left: 1px solid ${styleConstants.colors.gray_medium};
        `}
      >
        <HoverableLink
          css={css`
            display: flex;
            align-items: center;
            padding-top: 20px;
            padding-bottom: 20px;
          `}
          onClick={props.onUpdate}
          ariaLabel="Update"
          regular
          size={HoverableLinkSizes.Large}
        >
          {translateSingleString(props.locale, 'Update (${count})', {
            count: `${props.selectedVirtualCards.length}`,
          })}
        </HoverableLink>
      </TestId>
      <div
        css={css`
          display: flex;
          align-items: center;
          padding-left: 20px;
          border-left: 1px solid ${styleConstants.colors.gray_medium};
        `}
      >
        <MoreDropdown
          css={css`
            display: flex;
            position: relative;
            justify-content: center;
            align-items: center;
            width: 36px;
            height: 36px;

            > div:first-child > div {
              background-color: transparent !important;
            }

            &:hover > div:first-child > div svg g,
            &:hover > div:first-child > div svg path {
              fill: ${themeConstants.colors.cta_dark};
            }
          `}
          design={MoreDropdownDesigns.CtaToWhite}
          alignRight
          width={'300px'}
          bottomOptions={[
            {
              key: BulkAction.Deactivate,
              label: (
                <div
                  css={css`
                    ${getFontCss(
                      14,
                      styleConstants.colors.red,
                      styleConstants.fontFamilies.regular,
                    )};
                    display: flex;
                    align-items: center;
                    gap: 8px;
                  `}
                >
                  <SvgSlashCircle />
                  {translateSingleString(props.locale, 'Deactivate')}
                </div>
              ),
              onClick: props.onDeactivate,
              ariaLabelSource: 'Deactivate',
            },
            {
              key: BulkAction.CloseCards,
              label: (
                <div
                  css={css`
                    ${getFontCss(
                      14,
                      styleConstants.colors.red,
                      styleConstants.fontFamilies.regular,
                    )};
                    display: flex;
                    align-items: flex-start;
                    gap: 8px;
                  `}
                >
                  <SvgCloseCard
                    css={css`
                      flex-grow: 1;
                    `}
                    fill={styleConstants.colors.red}
                  />
                  <div>
                    {translateSingleString(props.locale, 'Close Virtual Cards')}
                    <div
                      css={css`
                        ${getFontCss(
                          12,
                          styleConstants.colors.gray_darkest,
                          styleConstants.fontFamilies.regular,
                        )};
                      `}
                    >
                      {translateSingleString(
                        props.locale,
                        'Permanently deactivate these virtual cards',
                      )}
                    </div>
                  </div>
                </div>
              ),
              onClick: props.onClose,
              ariaLabelSource: 'Close Virtual Cards',
            },
          ]}
          topOptions={[]}
          hover
          fillIcon
          openAbove
        />
      </div>
      <div
        css={css`
          display: flex;
          align-items: center;
          padding-left: 20px;
          border-left: 1px solid ${styleConstants.colors.gray_medium};
        `}
      >
        <HoverableLink
          ariaLabelSource={'Close'}
          onClick={props.onClearSelection}
        >
          <div
            css={css`
              svg path {
                stroke: ${themeConstants.colors.font_primary};
              }
              &:hover {
                svg path {
                  stroke: ${themeConstants.colors.cta_dark};
                }
              }
              height: 24px;
            `}
          >
            <SvgClose />
          </div>
        </HoverableLink>
      </div>
    </FloatingToolbar>
  )
}

export default VirtualCardsBulkActionsToolbar
